import { ElementRef, Injectable } from '@angular/core';
import { LinkTarget as LinkTargetReact } from '@office-start/common-types';
import { Document } from '@office-start/document-types';
import { getOtelReferralFromFileType } from '@office-start/file-utility';
import { WdOriginType as WdOriginTypeInst } from '@office-start/instrumentation-types';
import { logFeature, logUserAction } from '@office-start/telemetry-service';
import { Area, KnownFeatureInstrumentationEvents, KnownFeatureNames, Referral } from '@office-start/telemetry-types';
import { Subscription } from 'rxjs';

import { LinkTarget } from '../../shared/constants/link-target.enum';
import { InstrumentationData } from '../../shared/instrumentation/instrumentation-data.model';
import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownAreas, KnownInstrumentationEvents, KnownReferralPlatform } from '../../shared/instrumentation/instrumentation.service';
import { ErrorDialogErrorCategory } from '../../shared/models/error-dialog.model';
import { FileItem, FileType } from '../../shared/models/file-item.model';
import { KnownWorkloadReferralNames } from '../../shared/models/known-workload-referral-names.model';
import { WdOriginType } from '../../shared/models/telemetry-constants.model';
import { AccessTokenConsumer, AccessTokenService } from '../../shared/services/access-token.service';
import { DocumentService } from '../../shared/services/document.service';
import { LocalAppLauncherService } from '../../shared/services/local-app-launcher.service';
import { LaunchAction, UrlLauncherService } from '../../shared/services/url-launcher.service';
import { WacContentService } from '../../shared/services/wac-content.service';
import { CapabilitiesService } from '../../shared/shared-context/capabilities.service';
import { AuthType, ClientStartupConfigService } from '../../shared/shared-context/client-startup-config.service';
import { ContextService } from '../../shared/shared-context/context.service';
import { UrlBuilder } from '../../shared/utilities/url-builder.utility';
import { UploadSessionState, UploadSource } from '../models/upload-session.model';
import { UploadConfigService } from '../upload-config.service';
import { UploadManagerService } from '../upload-manager.service';
import { OneDriveUploadService } from '../upload.service';

@Injectable({
    providedIn: 'root'
})
export class UploadButtonService {
    private uploadSubscription: Subscription;

    constructor(
        private readonly accessTokenService: AccessTokenService,
        private readonly documentService: DocumentService,
        private readonly capabilitiesService: CapabilitiesService,
        private readonly contextService: ContextService,
        private readonly instrumentationService: InstrumentationService,
        private readonly localAppLauncherService: LocalAppLauncherService,
        private readonly startupConfigService: ClientStartupConfigService,
        private readonly uploadManagerService: UploadManagerService,
        private readonly uploadConfigService: UploadConfigService,
        private readonly oneDriveUploadService: OneDriveUploadService,
        private readonly urlLauncherService: UrlLauncherService,
        private readonly wacContentService: WacContentService
    ) {
    }

    public getClickDataForId(elementId: string, sessionId: string, uploadSource: UploadSource, buttonOrigin?: string, numDocuments?: number): InstrumentationData {
        const params: InstrumentationParams = {};
        params[InstrumentationParam.UploadSessionId] = sessionId;
        elementId = buttonOrigin ? `${elementId}-${buttonOrigin}` : elementId;
        params[InstrumentationParam.ElementId] = elementId;
        params[InstrumentationParam.Area] = this.contextService.isEdgeworth ? KnownAreas.Edgeworth : KnownAreas.Documents;
        params[InstrumentationParam.ItemCount] = (numDocuments || 0).toString();
        params[InstrumentationParam.ItemCategory] = UploadSource[uploadSource];

        const partnerCorrelationId = this.getPartnerCorrelationId();
        if (partnerCorrelationId !== undefined) {
            // Add Partner Correlation Id to telemetry if it exists as a query parameter.
            params[InstrumentationParam.PartnerCorrelationId] = partnerCorrelationId;
        }

        return {
            eventName: KnownInstrumentationEvents.UploadClick,
            params,
            metrics: []
        };
    }

    public uploadFile(file: File, uploadButton: ElementRef, sessionId: string, elementId: string, uploadSource: UploadSource) {
        const fileType = FileItem.fileTypeFromFileName(file.name);

        let clickLogged = false;
        const logClickHelper = (success: boolean, platform?: KnownReferralPlatform) => {
            if (clickLogged) {
                return;
            }
            clickLogged = true;
            this.logClick(elementId, sessionId, uploadSource, fileType, success, platform);
        };

        const uploadObservable = this.uploadManagerService.upload(file, this.oneDriveUploadService, sessionId, this.uploadConfigService.config.oneDriveResources, uploadSource);
        if (uploadObservable) {
            const startTimestamp = new Date().getTime();
            const startTime = startTimestamp.toString();
            let uploadComplete = false;
            this.uploadSubscription = uploadObservable.subscribe(session => {
                if (session.state === UploadSessionState.Completed) {
                    uploadComplete = true;
                    const params = {};
                    params[InstrumentationParam.UploadSessionId] = sessionId;
                    params[InstrumentationParam.Latency] = Math.ceil(session.duration).toString();
                    params[InstrumentationParam.ItemCategory] = UploadSource[uploadSource];

                    const partnerCorrelationId = this.getPartnerCorrelationId();
                    if (partnerCorrelationId !== undefined) {
                        // Add Partner Correlation Id to telemetry if it exists as a query parameter.
                        params[InstrumentationParam.PartnerCorrelationId] = partnerCorrelationId;
                    }

                    this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.UploadCompleted, params, [KnownInstrumentationEvents.UploadCompleted]);
                    logFeature({featureName: KnownFeatureNames.Upload, eventName: KnownFeatureInstrumentationEvents.UploadCompleted, params });

                    // 'wdNewAndOpenCt' is the time the user clicked on the menu item. This is used by the WAC apps
                    // to track the overall time it takes to land a user on a new document. In our case it will help
                    // track the time for the Graph API call along with the redirect to the WAC.
                    // If we are in UWP/PWA, we would have gotten the canonical URL in session.result from upload.service.ts
                    const addLoginHint = this.capabilitiesService.capabilities.outgoingLoginHintEnabled;
                    const canonicalUrl = UrlBuilder.appendLoginHint(session.result.canonicalUrl, this.startupConfigService.config.loginHint, addLoginHint);

                    const queryParams = {
                        wdNewAndOpenCt: startTime,
                        ct: startTime,
                        wdPreviousSession: this.startupConfigService.config.sharedClientStartupContext.correlationId,
                        wdOrigin: this.contextService.getWdOrigin(WdOriginType.Upload)
                    };

                    if (addLoginHint) {
                        queryParams['login_hint'] = this.startupConfigService.config.loginHint;
                    }

                    const webUrl = UrlBuilder.appendQueryParams(session.result.webUrl, queryParams);
                    const openFile = () => {
                        if (this.startupConfigService.config.sharedClientStartupContext.deploymentEnvironment === 'cloudtest') {
                            // On CloudTest, don't open a new tab, otherwise Selenium loses connection to the host document
                            // and the test fails intermittently.
                            return;
                        }

                        const launchWebUrl = (target: LinkTarget) => {
                            logClickHelper(true, KnownReferralPlatform.Web);
                            if (this.capabilitiesService.wacHostEnabledForFileType(fileType)) {
                                const fileModel = session.result;
                                this.wacContentService.bootstrapAndRoute(
                                    {
                                        docId: fileModel.docId,
                                        driveId: fileModel.driveId,
                                        docUrl: webUrl,
                                        fileType,
                                        wdOrigin: queryParams.wdOrigin
                                    },
                                    undefined,
                                    startTimestamp
                                );

                                // Tell the session that we opened the document within OfficeHome app so we need to dismiss the dialog
                                session.markCompletedAndOpenedInOfficeHome();
                            } else {
                                if (this.capabilitiesService.capabilities.contentLauncherEnabled) {
                                    import('@office-start/launcher-service').then(
                                        launchSvc => {
                                            launchSvc.launchUrl(webUrl, {action: 'Upload', linkTarget: target as LinkTargetReact});
                                        }
                                    );
                                } else {
                                    this.urlLauncherService.openUrl(webUrl, LaunchAction.Upload, target);
                                }
                            }
                        };

                        // launch in browser if on WXP page in Teams
                        if (this.capabilitiesService.capabilities.isEmbedMode && this.capabilitiesService.capabilities.embedModeEnabled) {
                            launchWebUrl(LinkTarget.NewTab);
                            return;
                        }

                        // If canonical URL is falsy, launch Web URL instead. Currently this is the case for MSA users uploading large files with Vroom
                        if (!canonicalUrl) {
                            launchWebUrl(this.capabilitiesService.capabilities.pwaEnabled ? LinkTarget.NewTab : LinkTarget.TopFrame);
                        } else {
                            if (this.capabilitiesService.capabilities.contentLauncherEnabled) {
                                import('@office-start/launcher-service').then(
                                    launchSvc => {
                                        const document: Document = {
                                            canonicalUrl,
                                            webUrl,
                                            app: FileType[fileType],
                                            attribution: '',
                                            url: canonicalUrl,
                                            extension: '',
                                            title: file.name,
                                            id: session.result.docId || ''
                                        };
                                        launchSvc.launchContent({
                                            action: 'Upload',
                                            document,
                                            useWebUrl: true,
                                            wdOriginType: WdOriginTypeInst.Upload
                                        }).then(result => {
                                            if (!result.success) {
                                                // Using NewTab here to allow completed dialog to show in mock mode. Has no effect in UWP.
                                                launchWebUrl(LinkTarget.NewTab);
                                                return;
                                            }
                                            logClickHelper(true, KnownReferralPlatform.Native);
                                        }).catch(() => launchWebUrl(LinkTarget.TopFrame));
                                    }
                                );
                            } else {
                                this.localAppLauncherService.launchOfficeApp(FileType[fileType], canonicalUrl).then(success => {
                                    if (!success) {
                                        // Using NewTab here to allow completed dialog to show in mock mode. Has no effect in UWP.
                                        launchWebUrl(LinkTarget.NewTab);
                                        return;
                                    }
                                    logClickHelper(true, KnownReferralPlatform.Native);
                                }).catch(() => launchWebUrl(LinkTarget.TopFrame));
                            }
                        }
                    };
                    const timeRemaining = this.uploadConfigService.config.minUploadDuration - session.duration;
                    if (timeRemaining <= 0) {
                        openFile();
                    } else {
                        setTimeout(openFile, timeRemaining);
                    }
                } else if (session.state === UploadSessionState.Cancelled) {
                    this.uploadSubscription.unsubscribe();
                    if (uploadButton) {
                        setTimeout(() => uploadButton.nativeElement.focus(), 0);
                    }
                    // We can end up in cancelled state after completion dialog is shown but before app launch finishes
                    if (!uploadComplete) {
                        logClickHelper(false);
                    }
                } else if (session.state === UploadSessionState.Failed) {
                    logClickHelper(false);
                }
            });
        }
    }

    public uploadFiles(files: FileList, sessionId: string, elementId: string, uploadSource: UploadSource) {
        const fileType = FileItem.fileTypeFromFileName(files[0].name);

        let clickLogged = false;
        const logClick = (success: boolean, platform?: KnownReferralPlatform) => {
            if (clickLogged) {
                return;
            }
            clickLogged = true;
            const clickData = this.getClickDataForId(elementId, sessionId, uploadSource);
            this.instrumentationService
                .logClickEvent(
                    clickData.eventName,
                    elementId,
                    clickData.params[InstrumentationParam.Area] as string,
                    success ? (this.uploadConfigService.config.uploadFileTypes.has(fileType) ? FileItem.referralFromFileType(fileType) : KnownWorkloadReferralNames.OneDrive) : '',
                    platform,
                    clickData.params,
                    clickData.metrics);
            logUserAction({
                eventName: clickData.eventName,
                id: elementId,
                area: clickData.params[InstrumentationParam.Area] as Area,
                result: success ? 'Refer' : 'Navigate',
                target: success ? (this.uploadConfigService.config.uploadFileTypes.has(fileType) ?  getOtelReferralFromFileType(fileType) : Referral.Documents) : 'Other'
            });
        };

        const uploadObservable = this.uploadManagerService.uploadFiles(files, sessionId, this.uploadConfigService.config.oneDriveResources, uploadSource);
        if (uploadObservable) {
            this.uploadSubscription = uploadObservable.subscribe(session => {
                // Multi upload is not currently supported so it will fail.
                if (session.state === UploadSessionState.Cancelled) {
                    this.uploadSubscription.unsubscribe();
                    logClick(false);
                } else if (session.state === UploadSessionState.Failed) {
                    logClick(false);
                }
            });
        }
    }

    public startUploadFlow(defaultExtensions: Array<string>, uploadSessionId: string, element: ElementRef<any>, elementId: string, uploadSource: UploadSource) {
        /*  For cases that require interactive login there are potentially multiple popup blockers that can kick in to disrupt this flow, so the ordering of these events matters.
            1. The browser will not allow the file dialog to open if it's not in direct response to a user action. Because of this, open the file dialog first and
               let the user pick a file.
            2. Trigger the login modal dialog if needed. This is done here instead of letting the auth token service trigger it automatically in order to not
               have multiple modal dialogs open at the same time (both the login and Upload modals).
            3. Once the user has re-authenticated, the login modal should automatically close and the upload dialog will then show.
        */
        this.documentService.selectFile(defaultExtensions).then(files => {
            const file = files[0];
            const fileType = FileItem.fileTypeFromFileName(file.name);
            const isMsa = this.startupConfigService.config.authType === AuthType.MSA;
            const tokenType = AccessTokenConsumer.Graph;
            const interactive = isMsa || this.uploadConfigService.config.capPopupEnabled;
            this.accessTokenService.getAccessToken(tokenType, false, null, interactive, { category: ErrorDialogErrorCategory.Upload, fileType })
                .then(() => {
                    this.uploadFile(file, element, uploadSessionId, elementId, uploadSource);
                });
        });
    }

    public logClick(elementId: string, sessionId: string, uploadSource: UploadSource, fileType: FileType, success: boolean, platform?: KnownReferralPlatform) {
        const clickData = this.getClickDataForId(elementId, sessionId, uploadSource);
        this.instrumentationService
            .logClickEvent(
                clickData.eventName,
                elementId,
                clickData.params[InstrumentationParam.Area] as string,
                success ? (this.uploadConfigService.config.uploadFileTypes.has(fileType) ? FileItem.referralFromFileType(fileType) : KnownWorkloadReferralNames.OneDrive) : '',
                platform,
                clickData.params,
                clickData.metrics);
            logUserAction({
                eventName: clickData.eventName,
                id: elementId,
                area: clickData.params[InstrumentationParam.Area] as Area,
                result: success ? 'Refer' : 'Navigate',
                target: success ? (this.uploadConfigService.config.uploadFileTypes.has(fileType) ?  getOtelReferralFromFileType(fileType) : Referral.Documents) : 'Other'
            });
    }

    private getPartnerCorrelationId() : string | undefined {
        const PartnerCorrelationIdQueryParameterName = 'partnerCid';
        const windowSearchParams = new URLSearchParams(window.location.search);

        return windowSearchParams.get(PartnerCorrelationIdQueryParameterName) ?? undefined;
    }
}
